import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

import { Separator } from "@/components/ui/separator";

import {
  CalendarDays,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";

import { getSchedulingThresholds, deriveHealthStatus } from "@/lib/scheduling-thresholds-storage";

import { type SchedulingSummary } from "@/lib/mock-scheduling-data";

// ---------------------------------------------------------------------------
// SchedulingOverviewWidget
// Displays a compact scheduling health summary card on the Overview tab.
// Driven by mock data in Phase 1; wire to API in Phase 2.
// ---------------------------------------------------------------------------

type SchedulingHealthStatus = SchedulingSummary["healthStatus"];

function getHealthBadgeStyle(status: SchedulingHealthStatus): {
  dot: string;
  label: string;
  text: string;
} {
  switch (status) {
    case "FullyResourced":
      return { dot: "bg-success-500", label: "Fully Resourced", text: "text-success-700" };
    case "PartiallyResourced":
      return { dot: "bg-warning-500", label: "Partially Resourced", text: "text-warning-700" };
    case "Unresourced":
      return { dot: "bg-error-500", label: "Unresourced", text: "text-error-700" };
    case "NoData":
    default:
      return { dot: "bg-muted-foreground", label: "No Data", text: "text-muted-foreground" };
  }
}

interface SchedulingOverviewWidgetProps {
  summary: SchedulingSummary;
  onViewDetails: () => void;
}

function SchedulingOverviewWidget({ summary, onViewDetails }: SchedulingOverviewWidgetProps) {
  // Derive health status from live thresholds so changes in Settings → Thresholds
  // are immediately reflected in the widget without a page reload.
  const thresholds = getSchedulingThresholds();
  const derivedStatus =
    summary.healthStatus === "NoData"
      ? "NoData"
      : deriveHealthStatus(summary.coveragePercent, thresholds);
  const health = getHealthBadgeStyle(derivedStatus);
  const coveragePct = Math.round(summary.coveragePercent);
  const totalUnmetHours = summary.unmetRoles.reduce((acc, r) => acc + r.gapHours, 0);

  return (
    <div>
      <Separator className="my-2" />
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base font-semibold">Resource Scheduling</CardTitle>
            </div>
            <div className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${health.dot}`} />
              <span className={`text-sm font-medium ${health.text}`}>{health.label}</span>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* KPI row */}
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs mb-0.5">Scheduled Hours</p>
              <p className="font-semibold">
                {summary.totalScheduledHours.toLocaleString()} / {summary.totalBudgetHours.toLocaleString()} hrs
              </p>
              <p className="text-xs text-muted-foreground">
                {summary.confirmedBookings} confirmed · {summary.plannedBookings} planned
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-0.5">Coverage</p>
              <p className="font-semibold">{coveragePct}%</p>
              <p className="text-xs text-muted-foreground">{summary.totalBookings} total bookings</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-0.5">Unfilled Hours</p>
              <p className={`font-semibold ${totalUnmetHours > 0 ? "text-warning-700" : "text-success-700"}`}>
                {totalUnmetHours > 0 ? `${totalUnmetHours} hrs` : "None"}
              </p>
              <p className="text-xs text-muted-foreground">
                {summary.unmetRoles.length > 0
                  ? `${summary.unmetRoles.length} role${summary.unmetRoles.length > 1 ? "s" : ""} unmet`
                  : "All roles covered"}
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="space-y-1">
            <Progress value={coveragePct} className="h-2" />
            <p className="text-xs text-muted-foreground">{coveragePct}% of budget hours scheduled</p>
          </div>

          {/* Warning banners */}
          {summary.warnings.length > 0 && (
            <div className="space-y-1.5">
              {summary.warnings.map((w, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-2 rounded-md px-3 py-2 text-xs ${
                    w.severity === "critical"
                      ? "bg-error-50 text-error-700 border border-error-200"
                      : w.severity === "warning"
                      ? "bg-warning-50 text-warning-700 border border-warning-200"
                      : "bg-info-50 text-info-700 border border-info-200"
                  }`}
                >
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>{w.message}</span>
                </div>
              ))}
            </div>
          )}

          {/* View Details link */}
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-primary hover:text-primary"
              onClick={onViewDetails}
            >
              View Details
              <ExternalLink className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export { SchedulingOverviewWidget, getHealthBadgeStyle };
export default SchedulingOverviewWidget;
